import React from 'react';

import Label from 'types/Label';
import { useSelector } from 'hooks';
import useLabelTranslation from 'hooks/useLabelTranslation';

import { getPayers } from 'modules/selfServices/selectors';
import type { Payer, RepairOrder, SelfService } from 'modules/selfServices/types/SelfService';

type UseRepairOrderProps = Pick<SelfService, 'repairOrder' | 'dealer' | 'valueAddedTax'>;

const getPayerNameById = (payers: Array<Payer>, payerId: string, getTranslation: (label: Label) => string) => {
  const payer = payers.find((currentPayer) => currentPayer.id === payerId);
  return getTranslation(payer?.name);
};

const sortByPayers = (getTranslation: (label: Label) => string, payers: Array<Payer>) => (
  (itemA: RepairOrder, itemB: RepairOrder) => {
    const hasPayerA = Boolean(itemA.payerId);
    const hasPayerB = Boolean(itemB.payerId);

    if (!hasPayerA && !hasPayerB) {
      return 0;
    }
    if (!hasPayerA) {
      return 1;
    }
    if (!hasPayerB) {
      return -1;
    }

    const aPayerName = getPayerNameById(payers, itemA.payerId, getTranslation);
    const bPayerName = getPayerNameById(payers, itemB.payerId, getTranslation);
    return aPayerName.localeCompare(bPayerName);
  }
);

const useRepairOrder = ({ repairOrder, valueAddedTax, dealer }: UseRepairOrderProps) => {
  const { getLabelTranslation } = useLabelTranslation();
  const payers = useSelector(getPayers);

  const sortedRepairOrders = React.useMemo(
    () => repairOrder?.toSorted?.(({ workOrderTag: a = '' }, { workOrderTag: b = '' }) => a.localeCompare(b)) ?? [],
    [repairOrder],
  );

  const sortedRepairOrderItems = React.useMemo(
    () => sortedRepairOrders.map<RepairOrder>((repairOrderItem) => ({
      ...repairOrderItem,
      vat: valueAddedTax,
      currency: dealer.currencyIsoCode,
      translatedName: getLabelTranslation(repairOrderItem.adviceName),
      translatedDescription: repairOrderItem.description ? getLabelTranslation(repairOrderItem.description) : undefined,
    })).toSorted(sortByPayers(getLabelTranslation, payers)),
    [sortedRepairOrders, valueAddedTax, dealer, payers, getLabelTranslation],
  );

  return React.useMemo(() => ({
    sortedRepairOrders,
    sortedRepairOrderItems,
    hasRepairOrder: sortedRepairOrderItems.length > 0,
  }), [sortedRepairOrders, sortedRepairOrderItems]);
};

export default useRepairOrder;
